import { cn } from "@/lib/utils";
import { type ClassValue } from "clsx";
import { type ElementType, type JSX, type ReactNode } from "react";

type IntrinsicElement = keyof JSX.IntrinsicElements;

type CardProps<T extends IntrinsicElement> = {
  readonly as?: T;
} & JSX.IntrinsicElements[T];

function Card<T extends IntrinsicElement = "article">({
  as = "article" as T,
  className,
  children,
  ...props
}: CardProps<T>) {
  const Component = as as ElementType;

  return (
    <Component
      className={cn(
        "overflow-hidden rounded-xl border border-gray-200 bg-white p-4 text-gray-800 shadow-[0_3px_4px_0_rgba(0,0,0,0.03)] lg:p-6 xl:p-8",
        className,
      )}
      {...props}
    >
      {children}
    </Component>
  );
}

interface Props {
  readonly className?: ClassValue;
  readonly children: ReactNode;
}

function CardHeader({ className, children }: Props) {
  return (
    <div
      className={cn(
        "-mx-4 -mt-4 border-b border-gray-300 bg-white p-4 lg:-mx-6 lg:-mt-6 lg:p-6 xl:-mx-8 xl:-mt-8 xl:p-8",
        className,
      )}
    >
      {children}
    </div>
  );
}

function CardContent({ className, children }: Props) {
  return (
    <div className={cn("py-4 lg:py-6 xl:py-8", className)}>{children}</div>
  );
}

function CardFooter({ className, children }: Props) {
  return (
    <div
      className={cn(
        "-mx-4 -mb-4 border-t border-gray-300 bg-white p-4 lg:-mx-6 lg:-mb-6 lg:p-6 xl:-mx-8 xl:-mb-8 xl:p-8",
        className,
      )}
    >
      {children}
    </div>
  );
}

function CardTitle({ className, children }: Props) {
  return (
    <h2 className={cn("text-lg/none font-semibold text-gray-800", className)}>
      {children}
    </h2>
  );
}

function CardSubtitle({ className, children }: Props) {
  return (
    <p className={cn("text-sm/none font-semibold text-gray-500", className)}>
      {children}
    </p>
  );
}

export { Card, CardContent, CardFooter, CardHeader, CardSubtitle, CardTitle };
